/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect} from "react";
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox";
import ArgosTextField from "../../../../../../components/Form/TextField/ArgosTextField";
import {useAppDispatch, useAppSelector} from "../../../../../../../redux/hooks";
import {
  selectConfigSensorWizardDefinePropertiesStep,
  selectConfigSensorWizardSelectSensorStep,
  selectConfigSensorWizardState,
  selectSensorBomData,
  setConfigSensorWizardDefinePropertiesStep,
  TDefinePropertiesStep,
} from "../../../../../../../redux/ducks/AdasPrograms/slice";
import {FormControl, FormHelperText, InputAdornment} from "@mui/material";
import {PROPERTY_FIELDS, PROPERTY_KEY, PropertyField} from "./constants";
import {cloneDeep} from "lodash";


/** Step name to display in wizard */
export const DEFINE_PROPERTIES_STEP_NAME = 'Define properties'


export const DefinePropertiesStep = React.memo(() => {
  const dispatch = useAppDispatch()

  const wizardState = useAppSelector(selectConfigSensorWizardState)
  const stepState = useAppSelector(selectConfigSensorWizardDefinePropertiesStep)
  const selectSensorStepState = useAppSelector(selectConfigSensorWizardSelectSensorStep)

  /** Check sensor BoM data if properties were previously set for the sensor */
  const propertiesData = useAppSelector(state => {
    const sensorId = selectSensorStepState?.id
    let locationKey = selectSensorStepState?.locationKey

    if (typeof locationKey === 'string') {
      locationKey = [locationKey]
    }

    if (sensorId && locationKey) {
      const sensorData = selectSensorBomData(state)?.data?.sensors?.[sensorId]?.sensorLocations?.filter((loc: any) =>
        loc.locationKey === locationKey?.[0]
      )[0]
      const properties = sensorData?.properties
      if (properties) {
        return properties
      }
    }
    return undefined
  })

  /** Set state with previous property state if they exist */
  useEffect(() => {
    if (propertiesData) {
      const transformedProperties = Object.entries(propertiesData).reduce((acc: { [key: string]: { value: string, error: string | null } }, [key, value]) => {
        acc[key] = {value: String(value), error: null}
        return acc
      }, {})
      handleChange({
        ...stepState,
        ...transformedProperties,
      })
    }
  }, [propertiesData])

  /** Update form state when changing value */
  const handleChange = (data: any) => {
    dispatch(setConfigSensorWizardDefinePropertiesStep(data))
  }

  const definePropertiesStepState = useAppSelector(selectConfigSensorWizardDefinePropertiesStep)
  const [isValidating, setIsValidating] = React.useState<boolean>(false)

  /** Validate form */
  const validateProperties = () => {
    setIsValidating(true)
    let isValid = true
    const newStepState = cloneDeep(definePropertiesStepState)
    if (!newStepState) return

    for (const key in newStepState) {
      const property = key as keyof TDefinePropertiesStep
      /** Check for blank fields */
      if (newStepState[property].value === "" || newStepState[property].value === null || newStepState[property].value === undefined) {
        isValid = false
        newStepState[property].error = 'Property is required'
      }
      /** Check if angle values are within allowed range */
      else if (PROPERTY_FIELDS.find(field => PROPERTY_KEY[field.id] === property)?.unit === "deg") {
        const angleValue = parseFloat(newStepState[property].value)
        const isSensorDesiredFov = property === 'sensorDesiredFovDegrees'
        const minVal = isSensorDesiredFov ? -179 : -180
        const maxVal = isSensorDesiredFov ? 179 : 180
        if (isNaN(angleValue) || angleValue < minVal || angleValue > maxVal) {
          isValid = false
          newStepState[property].error = `Value must be between ${minVal} and ${maxVal}`
        } else {
          newStepState[property].error = null
        }
      }
      /** Check if thickness values are within allowed range */
      else if (property === 'materialThicknessNominal' || property === 'materialThicknessMinimum' || property === 'materialThicknessMaximum') {
        const value = parseFloat(newStepState[property].value)
        if (isNaN(value) || value < 0) {
          isValid = false
          newStepState[property].error = 'Value must be greater than or equal to 0'
        } else {
          newStepState[property].error = null
        }
      }
      else if (property === 'materialPerformanceDegradation') {
        const value = parseFloat(newStepState[property].value)
        if (isNaN(value) || value > 0) {
          isValid = false
          newStepState[property].error = 'Value must be less than or equal to 0'
        } else {
          newStepState[property].error = null
        }
      }
      else {
        newStepState[property].error = null
      }
    }


    /**
     * Validates that the minimum, nominal, and maximum properties are within allowed ranges.
     *
     * @param {string} baseName - The base name of the property to validate. This will be used to construct the full property names for minimum, nominal, and maximum values.
     */
    const validateMinNominalMax = (baseName: string) => {
      const minProp = `${baseName}Minimum` as keyof TDefinePropertiesStep
      const nominalProp = `${baseName}Nominal` as keyof TDefinePropertiesStep
      const maxProp = `${baseName}Maximum` as keyof TDefinePropertiesStep

      const minValue = parseFloat(newStepState[minProp]?.value || '')
      const nominalValue = parseFloat(newStepState[nominalProp]?.value || '')
      const maxValue = parseFloat(newStepState[maxProp]?.value || '')

      if (!isNaN(minValue) && !isNaN(nominalValue) && !isNaN(maxValue)) {
        if (minValue > nominalValue && nominalValue > maxValue) {
          isValid = false
          newStepState[minProp].error = 'Minimum must be less than or equal to Nominal'
          newStepState[nominalProp].error = 'Nominal must be greater than or equal to Minimum and less than or equal to Maximum'
          newStepState[maxProp].error = 'Maximum must be greater than or equal to Nominal'
        } else if (minValue > nominalValue) {
          isValid = false
          newStepState[minProp].error = 'Minimum must be less than or equal to Nominal'
          newStepState[nominalProp].error = 'Nominal must be greater than or equal to Minimum'
        } else if (nominalValue > maxValue) {
          isValid = false
          newStepState[nominalProp].error = 'Nominal must be less than or equal to Maximum'
          newStepState[maxProp].error = 'Maximum must be greater than or equal to Nominal'
        }
      }
    }
    const baseNames = ['x', 'y', 'z', 'roll', 'pitch', 'yaw']
    /** Properties that have min, nominal, and max values */
    baseNames.forEach(baseName => validateMinNominalMax(baseName))

    dispatch(setConfigSensorWizardDefinePropertiesStep(newStepState))
    setIsValidating(false)
    return isValid
  }

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%', gap: 20, padding: '10px'}}>
      {PROPERTY_FIELDS.map((field: PropertyField) => (
        <FormControl key={field.id} variant={'standard'}>
          <ArgosTextField
            id={`${field.id}-textfield`}
            inputProps={{'data-testid': `${field.id}-textfield`}}
            label={field.label}
            required={field.required}
            value={stepState?.[PROPERTY_KEY[field.id]].value}
            error={!!(stepState?.[PROPERTY_KEY[field.id]].error ?? false)}
            aria-describedby={!!(stepState?.[PROPERTY_KEY[field.id]].error ?? false)
              ? `${field.id}-helper-text`
              : undefined
            }
            type='number'
            variant='standard'
            InputProps={{
              endAdornment: <InputAdornment position='end'>{field.unit}</InputAdornment>
            }}
            onChange={(e) => {
              handleChange({
                ...stepState,
                [PROPERTY_KEY[field.id]]: {...stepState?.[PROPERTY_KEY[field.id]], value: e.target.value}
              })
            }}
            onBlur={() => {
              validateProperties()
            }}
          />
          {!!(stepState?.[PROPERTY_KEY[field.id]].error ?? false)
            ? <FormHelperText id={`${field.id}-helper-text`} error>
              {stepState?.[PROPERTY_KEY[field.id]].error}
            </FormHelperText>
            : null
          }
        </FormControl>
      ))}
    </ArgosFlexbox>
  )
})
